import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import XMLID from '../../assets/images/XMLID.svg'
import { useIntl } from 'react-intl'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { useThemeMode } from '../../../../../_metronic/partials'
import brandingConfig from '../../../../../config/brandingConfig'
import { RecordSync, storageIcons } from '../../core/_constants'
import { RecordKeeperSourceStorageType } from '../../core/_models'
import { useAuth } from '../../../auth'
import { getSourceReferenceData } from '../../core/_requests'
import Skeleton from 'react-loading-skeleton'
import { useNavigate } from 'react-router-dom'

export const StorageDropdown = ({
  selectedStorage,
  setSelectedStorage,
  isLoading,
  setIsLoading
}: {
  selectedStorage: string[] | []
  setSelectedStorage: Dispatch<SetStateAction<string[] | []>>
  isLoading: boolean,
  setIsLoading: Dispatch<SetStateAction<boolean>>
}) => {
  const { companyId } = useAuth()
  const navigate = useNavigate()
  const [active, setActive] = useState(false)
  const { formatMessage } = useIntl()
  const { mode } = useThemeMode()
  const [selectAll, setSelectAll] = useState(true)
  const [checkedOptions, setCheckedOptions] = useState<boolean[] | []>([false])
  const [storageOptions, setStorageOptions] = useState<RecordKeeperSourceStorageType[] | []>([])
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const [filteredOptions, setFilteredOptions] = useState<RecordKeeperSourceStorageType[]>([])

  async function fetchSourceStorage() {
    setIsLoading(true)
    try {
      const {
        data: { success, data, errors },
      } = await getSourceReferenceData('recordKeeperSourceStorageList', companyId!)
      if (success) {
        if (data.length === 0) {
          setSelectAll(false)
        }
        setCheckedOptions(Array(data.length).fill(true))
        setSelectedStorage(data.map((option: RecordKeeperSourceStorageType) => option.sourceId))
        setStorageOptions(data)
        setFilteredOptions(data)
      } else {
        console.error(errors)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSourceStorage()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectAllChange = (event: any) => {
    if (event.target.checked) {
      setSelectedStorage(filteredOptions.map((option) => option.sourceId))
    } else {
      setSelectedStorage([])
    }
    const newSelectAll = !selectAll
    setSelectAll(newSelectAll)

    setCheckedOptions(Array(storageOptions.length).fill(newSelectAll))
  }

  const handleCheckboxChange = (event: any, index: any, storage: string) => {

    if (event.target.checked) {
      setSelectedStorage([...selectedStorage, storage])
    } else {
      setSelectedStorage(selectedStorage.filter((item) => item !== storage))
    }

    const newCheckedOptions = [...checkedOptions]
    newCheckedOptions[index] = !newCheckedOptions[index]

    setCheckedOptions(newCheckedOptions)
    setSelectAll(newCheckedOptions.every(Boolean))
  }

  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActive(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFilteredOptions(
      storageOptions.filter((option: RecordKeeperSourceStorageType) =>
        option.storageName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    )
  }, [searchQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={dropdownRef}>
      <div
        className='d-flex align-items-center justify-content-between gap-1 pb-4 mb-4'
        style={mode === 'dark' ? { borderBottom: '1px dotted #565674' } : { borderBottom: '1px dotted #DBDFE9' }}
        onClick={() => {
          console.log('clicked', active);

          setActive((prev) => !prev)
        }}

      >
        <div className='d-flex justify-content-start gap-2 align-items-center'>
          <div
            className='rounded-circle d-flex justify-content-center align-items-center'
            style={{
              background: '#EDF6FF',
              border: '1px solid #4776E666',
              height: '40px',
              width: '40px',
            }}
          >
            <img
              src={brandingConfig.favicon}
              alt={`${brandingConfig.brandName} Logo`}
              height={20}
              width={20}
            />
          </div>
          <div>
            <h3 className='font-size-13 m-0 mb-1'>
              {formatMessage({ id: brandingConfig.brandName })}
            </h3>
            <div className='d-flex gap-1 align-items-center'>
              <img src={XMLID} alt='XMLID' />
              {selectedStorage.length > 0 && (
                <>
                  {selectedStorage.slice(0, 3).map((storage, index) => (
                    <img
                      key={index}
                      src={toAbsoluteUrl(
                        `/media/icons/RecordsKeeperAI/${storageIcons[
                        storageOptions.find((storageData) => storageData.sourceId === storage)
                          ?.cloudType!
                        ]
                        }.svg`
                      )}
                      alt={storage}
                      height={12}
                      width={12}
                    />
                  ))}
                </>
              )}
              <span className='font-size-13 text-muted m-0'>
                {selectedStorage.length > 3 ? `& ${selectedStorage.length - 3} More ${storageOptions.length === 1 ? 'storage' : 'storages'}` : ''}
              </span>
            </div>
          </div>
        </div>
        <div>
          {active ? <i className='fa-solid fa-chevron-down'></i> : <i className='fa-solid fa-chevron-up'></i>}
        </div>
      </div>
      {active && (
        <div
          className='border p-4 rounded bg-white position-absolute shadow overflow-auto'
          style={{
            zIndex: '4',
            boxShadow: '0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075) !important',
            width: '325px',
            minHeight: "240px"
          }}
        >
          <div className='border border-1 rounded-3'>
            <form
              data-kt-search-element='form'
              className=' align-items-center d-flex position-relative'
              autoComplete='off'
            >
              <KTSVG
                path='/media/icons/duotune/general/gen021.svg'
                className='svg-icon-3 position-absolute ms-3'
              />
              <input
                type='text'
                className='form-control ps-10 h-40px rounded-2 font-weight-400 outline-none border-0'
                name='search'
                placeholder={formatMessage({ id: 'Search' })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-kt-search-element='input'
              />
            </form>
          </div>
          <div
            className='d-flex align-items-center gap-3 py-4 px-2'
            style={
              mode === 'dark'
                ? { borderBottom: '1px dotted #565674' }
                : { borderBottom: '1px dotted #DBDFE9' }
            }
          >
            <div
              className='d-flex align-items-center justify-content-center rounded-circle'
              style={{
                background: '#EDF6FF',
                border: '1px solid #4776E666',
                height: '35px',
                width: '35px',
              }}
            >
              <img className='' height={16} width={16} src={XMLID} alt='XMLID' />
            </div>
            <div className='mt-3'>
              <h1 className='font-size-13 m-0'>{formatMessage({ id: 'Record Keeper' })}</h1>
              <p className='font-size-10 m-0 text-muted'>
                {formatMessage({ id: 'Default Storage' })}
              </p>
            </div>
          </div>
          <div className='h-350px overflow-y-scroll'>
            <div className='d-flex align-items-center gap-3 py-3'>
              <input
                type='checkbox'
                className='form-check-input h-19px w-19px mt-1'
                checked={selectAll}
                onChange={handleSelectAllChange}
              />

              <p className='m-0 mt-2 font-size-13 text-muted'>
                {`Select All ${storageOptions ? storageOptions.length : 0} ${storageOptions.length === 1 ? 'Storage' : 'Storages'}`}
              </p>
            </div>
            {isLoading ? (
              <>
                <div className='mb-3'>
                  <Skeleton
                    height={24}
                    width={'10vw'}
                    className='my-2'
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                </div>
                <div className='mb-3'>
                  <Skeleton
                    height={24}
                    width={'10vw'}
                    className='my-2'
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                </div>
                <div className='mb-3'>
                  <Skeleton
                    height={24}
                    width={'10vw'}
                    className='my-2'
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                </div>
              </>
            ) : (
              <>
                {filteredOptions.filter((filteringData) => filteringData.cloudType !== RecordSync.LOCAL).map((option, index) => (
                  <div
                    key={index}
                    className='d-flex align-items-center gap-3 py-4 px-2'
                    style={
                      mode === 'dark'
                        ? { borderBottom: '1px dotted #565674' }
                        : { borderBottom: '1px dotted #DBDFE9' }
                    }
                  >
                    <input
                      type='checkbox'
                      className='form-check-input h-25px w-25px mt-1'
                      checked={checkedOptions[index]}
                      onChange={(event) => handleCheckboxChange(event, index, option.sourceId)}
                    />
                    <div
                      className='d-flex justify-content-center align-items-center rounded-circle'
                      style={{
                        background: '#EDF6FF',
                        border: '1px solid #4776E666',
                        height: '35px',
                        width: '35px',
                      }}
                    >
                      <img
                        className=''
                        height={16}
                        width={16}
                        src={toAbsoluteUrl(
                          `/media/icons/RecordsKeeperAI/${storageIcons[option.cloudType]}.svg`
                        )}
                        alt={option.storageName}
                      />
                    </div>
                    <div className='mt-3'>
                      <h1 className='font-size-13 m-0'>{formatMessage({ id: option.storageName })}</h1>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          <div>
            <button className='btn bg-primary text-white custom-button-property w-100 mt-4'
              onClick={() => navigate('/rk/sources-and-sync')}>
              {formatMessage({ id: 'Link Cloud Storage' })}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
